// widgetConfigurations/RecordFinderConfig.js
import React, { useEffect, useState } from "react";
import {
  Box,
  TextField,
  MenuItem,
  Button,
  Modal,
  Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";

const pools = [
  {
    poolName: "Pool 1",
    poolPercentage: "92%",
    poolStatus: "PASSED",
    poolColor: "blue",
  },
  {
    poolName: "Pool 2",
    poolPercentage: "82%",
    poolStatus: "FAILED",
    poolColor: "red",
  },
  {
    poolName: "Pool 3",
    poolPercentage: "82%",
    poolStatus: "FAILED",
    poolColor: "red",
  },
];

const CertificationsConfig = ({
  data,
  onSubmit,
  onDelete,
  isEditing,
  workspace,
}) => {
  const { control, handleSubmit, watch, setValue } = useForm({
    defaultValues: {
      ...data,
      minWidth: 4,
      maxWidth: 6,
      minHeight: 10,
      defaultWidth: 4,
      defaultHeight: 13,
      pools: pools,
      dynamicView: data?.dynamicView || [], // Set as an array for multiple selections
    },
  });

  useEffect(() => {
    if (data) {
      Object.entries(data).forEach(([key, value]) => {
        setValue(key, value);
      });
    }
  }, [data, setValue]);

  const dynamicView = watch("dynamicView", []); // Watch dynamicView changes

  useEffect(() => {
    // Adjust the height dynamically based on the number of dynamic views
    const dynamicViewCount = dynamicView.length;
    const additionalHeight = dynamicViewCount > 3 ? dynamicViewCount - 3 : 0;
    setValue("defaultHeight", 13 + additionalHeight); // Update the default height
  }, [dynamicView, setValue]);

  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [sections, setSections] = useState([]);

  useEffect(() => {
    // Fetch available sections for the specific workspace
    const fetchSections = async () => {
      if (workspace?.configuration?.sections) {
        setSections(workspace.configuration.sections);
      }
    };

    fetchSections();
  }, [workspace]);

  const handleClearSection = () => {
    setValue("sectionId", ""); // Clear the sectionId field
  };

  const handleOpenDeleteModal = () => setOpenDeleteModal(true);
  const handleCloseDeleteModal = () => setOpenDeleteModal(false);
  const handleConfirmDelete = () => {
    handleCloseDeleteModal();
    onDelete();
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Box mb={2}>
        <Controller
          name="widgetName"
          control={control}
          defaultValue=""
          render={({ field }) => (
            <TextField {...field} label="Widget Name" fullWidth />
          )}
        />
      </Box>
      <Box mb={2}>
        <Controller
          name="performedFor"
          control={control}
          defaultValue=""
          render={({ field }) => (
            <TextField {...field} label="Performed For" fullWidth />
          )}
        />
      </Box>
      {pools.map((pool, index) => (
        <Box key={index} mb={2}>
          <Box mb={1}>
            <Controller
              name={`pools[${index}].poolName`}
              control={control}
              defaultValue={pool[`poolName${index + 1}`] || ""}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={`Pool ${index + 1} Name`}
                  fullWidth
                />
              )}
            />
          </Box>
          <Box mb={1}>
            <Controller
              name={`pools[${index}].poolPercentage`}
              control={control}
              defaultValue={pool[`poolPercentage${index + 1}`] || ""}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={`Pool ${index + 1} Percentage`}
                  fullWidth
                />
              )}
            />
          </Box>
          <Box mb={1}>
            <Controller
              name={`pools[${index}].poolStatus`}
              control={control}
              defaultValue={pool[`poolStatus${index + 1}`] || ""}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={`Pool ${index + 1} Status`}
                  fullWidth
                />
              )}
            />
          </Box>
          <Box mb={1}>
            <Controller
              name={`pools[${index}].poolColor`}
              control={control}
              defaultValue={pool[`poolColor${index + 1}`] || ""}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={`Pool ${index + 1} Color`}
                  fullWidth
                />
              )}
            />
          </Box>
        </Box>
      ))}

      {sections.length > 0 && (
        <Box mb={2}>
          <Controller
            name="sectionId"
            control={control}
            defaultValue=""
            render={({ field }) => (
              <TextField {...field} label="Select Section" select fullWidth>
                {sections.map((section) => (
                  <MenuItem key={section.id} value={section.id}>
                    {section.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
          <Button
            variant="contained"
            onClick={handleClearSection}
            sx={{
              backgroundColor: "rgb(18, 17, 53)",
              "&:hover": {
                backgroundColor: "rgba(18, 17, 53, 0.8)",
              },
            }}
          >
            Clear Section
          </Button>
        </Box>
      )}

      <Button
        variant="contained"
        type="submit"
        sx={{
          backgroundColor: "rgb(18, 17, 53)",
          "&:hover": {
            backgroundColor: "rgba(18, 17, 53, 0.8)",
          },
          marginRight: "1rem",
        }}
      >
        {data && isEditing ? "Save Widget" : "Update Preview"}
      </Button>

      {onDelete && (
        <>
          <Button
            variant="contained"
            type="button"
            onClick={handleOpenDeleteModal}
          >
            Remove Widget
          </Button>

          <Modal
            open={openDeleteModal}
            onClose={handleCloseDeleteModal}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
          >
            <Box
              position="absolute"
              top="50%"
              left="50%"
              transform="translate(-50%, -50%)"
              width={400}
              bgcolor="background.paper"
              boxShadow={24}
              p={4}
            >
              <Typography id="modal-modal-title" variant="h6" component="h2">
                Confirm Delete
              </Typography>
              <Typography id="modal-modal-description" sx={{ mt: 2 }}>
                Are you sure you want to delete this widget?
              </Typography>
              <Box mt={4} display="flex" justifyContent="space-between">
                <Button
                  onClick={handleConfirmDelete}
                  variant="contained"
                  color="secondary"
                  sx={{
                    backgroundColor: "rgb(18, 17, 53)",
                    "&:hover": {
                      backgroundColor: "rgba(18, 17, 53, 0.8)",
                    },
                  }}
                >
                  Confirm
                </Button>
                <Button onClick={handleCloseDeleteModal} variant="outlined">
                  Cancel
                </Button>
              </Box>
            </Box>
          </Modal>
        </>
      )}
    </form>
  );
};

export default CertificationsConfig;
